import { cloud, supabase } from "./cloud.js";
import { compressImage } from "./image-utils.js";

const encoder = new TextEncoder();

async function digestHex(value) {
  const bytes = new Uint8Array(await crypto.subtle.digest("SHA-256", encoder.encode(value)));
  return [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function stableUuid(seed) {
  const hex = await digestHex(seed);
  const chars = hex.slice(0, 32).split("");
  chars[12] = "5";
  chars[16] = ((parseInt(chars[16], 16) & 3) | 8).toString(16);
  const value = chars.join("");
  return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
}

function legacyTypeName(moment, types) {
  return String(moment.type || types.find((type) => type.id === moment.recordTypeId)?.name || "").trim();
}

function legacyTagNames(moment, tags) {
  if (Array.isArray(moment.tags)) return moment.tags.map(String);
  return (moment.tagIds || []).map((id) => tags.find((tag) => tag.id === id)?.name).filter(Boolean);
}

export async function legacyFingerprint(snapshot) {
  const identity = {
    moments: snapshot.moments.map((item) => [item.id, item.createdAt, item.content || item.body]),
    types: snapshot.types.map((item) => [item.id, item.name]),
    tags: snapshot.tags.map((item) => [item.id, item.name]),
    closures: Object.keys(snapshot.closures).sort(),
    images: snapshot.imageIds.slice().sort(),
  };
  return digestHex(JSON.stringify(identity));
}

export function legacyCounts(snapshot) {
  return {
    moments: snapshot.moments.length,
    images: snapshot.imageIds.length,
    types: snapshot.types.length,
    tags: snapshot.tags.length,
    closures: Object.keys(snapshot.closures).length,
    links: snapshot.moments.filter((item) => item.sourceMomentId || item.sourceId).length,
  };
}

export async function migrateLegacySnapshot(snapshot, loadImage, onProgress = () => {}) {
  if (!cloud.state.user || !supabase) throw new Error("请先登录需要接收数据的账号");
  const userId = cloud.state.user.id;
  const fingerprint = await legacyFingerprint(snapshot);
  const prior = await cloud.migrationCompleted(fingerprint);
  if (prior) return { fingerprint, counts: prior.counts, alreadyCompleted: true };
  const expected = legacyCounts(snapshot);
  const typeMap = new Map();
  const tagMap = new Map();
  const momentMap = new Map();

  onProgress("正在对应记录类型…");
  const cloudTypes = cloud.state.snapshot.types;
  for (let index = 0; index < snapshot.types.length; index += 1) {
    const legacy = snapshot.types[index];
    const name = String(legacy.name || "").trim();
    if (!name) continue;
    const existing = cloudTypes.find((item) => item.name === name);
    if (existing) {
      const { error } = await supabase.from("record_types").update({ is_archived: Boolean(legacy.isArchived), sort_order: Number(legacy.sortOrder || legacy.sort_order || existing.sortOrder || (index + 1) * 10) }).eq("id", existing.id);
      if (error) throw error;
      typeMap.set(String(legacy.id), existing.id);
      continue;
    }
    const id = await stableUuid(`${userId}:${fingerprint}:type:${legacy.id || name}`);
    const { error } = await supabase.from("record_types").upsert({ id, user_id: userId, name, icon: legacy.icon || "leaf", description: legacy.description || "由本机记录迁移", sort_order: 500 + index * 10, is_archived: Boolean(legacy.isArchived), is_system: false }, { onConflict: "id" });
    if (error) throw error;
    typeMap.set(String(legacy.id), id);
  }

  onProgress("正在迁移主题标签…");
  for (const legacy of snapshot.tags) {
    const name = String(legacy.name || "").trim();
    if (!name) continue;
    const existing = cloud.state.snapshot.tags.find((item) => item.name === name);
    const id = existing?.id || await stableUuid(`${userId}:${fingerprint}:tag:${legacy.id || name}`);
    if (existing) {
      const { error } = await supabase.from("tags").update({ is_archived: Boolean(legacy.isArchived) }).eq("id", existing.id);
      if (error) throw error;
    } else {
      const { error } = await supabase.from("tags").upsert({ id, user_id: userId, name, is_archived: Boolean(legacy.isArchived) }, { onConflict: "id" });
      if (error) throw error;
    }
    tagMap.set(String(legacy.id), id);
  }

  for (const moment of snapshot.moments) momentMap.set(String(moment.id), await stableUuid(`${userId}:${fingerprint}:moment:${moment.id}`));

  onProgress("正在迁移 Moment…");
  for (let index = 0; index < snapshot.moments.length; index += 1) {
    const legacy = snapshot.moments[index];
    const oldId = String(legacy.id);
    const typeName = legacyTypeName(legacy, snapshot.types);
    const typeId = typeMap.get(String(legacy.recordTypeId || "")) || cloud.state.snapshot.types.find((item) => item.name === typeName)?.id || null;
    const tagIds = legacyTagNames(legacy, snapshot.tags).map((name) => {
      const tag = snapshot.tags.find((item) => item.name === name);
      return tagMap.get(String(tag?.id || ""));
    }).filter(Boolean);
    await cloud.helpers.importCore({ id: momentMap.get(oldId), content: String(legacy.content || legacy.body || "").trim(), recordTypeId: typeId, tagIds, occurredAt: legacy.occurredAt || legacy.occurrenceAt, createdAt: legacy.createdAt, sourceMomentId: null });

    const refs = (legacy.images || []).filter((ref) => String(ref).startsWith("img:"));
    for (let imageIndex = 0; imageIndex < refs.length; imageIndex += 1) {
      const stored = await loadImage(refs[imageIndex]);
      if (!stored?.blob) throw new Error(`Moment ${oldId} 的第 ${imageIndex + 1} 张图片无法读取`);
      const compressed = await compressImage(new File([stored.blob], stored.name || "生活图片", { type: stored.type || stored.blob.type }));
      const imageId = await stableUuid(`${userId}:${fingerprint}:image:${refs[imageIndex]}`);
      await cloud.helpers.upload(momentMap.get(oldId), { ...compressed, cloudId: imageId }, imageIndex, imageId);
    }
    onProgress(`正在迁移 Moment · ${index + 1} / ${snapshot.moments.length}`);
  }

  onProgress("正在恢复来源关系…");
  for (const legacy of snapshot.moments) {
    const oldSource = legacy.sourceMomentId || legacy.sourceId;
    if (!oldSource || !momentMap.has(String(oldSource))) continue;
    const cloudMoment = cloud.state.snapshot.moments.find((item) => item.id === momentMap.get(String(legacy.id)));
    const typeName = legacyTypeName(legacy, snapshot.types);
    const typeId = typeMap.get(String(legacy.recordTypeId || "")) || cloud.state.snapshot.types.find((item) => item.name === typeName)?.id || null;
    const tagIds = legacyTagNames(legacy, snapshot.tags).map((name) => {
      const tag = snapshot.tags.find((item) => item.name === name);
      return tagMap.get(String(tag?.id || ""));
    }).filter(Boolean);
    await cloud.helpers.importCore({ id: momentMap.get(String(legacy.id)), content: legacy.content || legacy.body, recordTypeId: typeId, tagIds, occurredAt: legacy.occurredAt || legacy.occurrenceAt, createdAt: legacy.createdAt, sourceMomentId: momentMap.get(String(oldSource)), images: cloudMoment?.images || [] });
  }

  onProgress("正在迁移每日收束…");
  for (const [date, closure] of Object.entries(snapshot.closures)) await cloud.saveClosure(date, { answers: closure.answers || [], finalLine: closure.finalLine || closure.final_sentence || "" });

  const refreshed = await cloud.refresh();
  const importedMomentIds = new Set(momentMap.values());
  const imported = refreshed.moments.filter((item) => importedMomentIds.has(item.id));
  const actual = {
    moments: imported.length,
    images: imported.reduce((sum, item) => sum + item.images.length, 0),
    closures: Object.keys(snapshot.closures).filter((date) => refreshed.closures[date]).length,
    links: imported.filter((item) => item.sourceMomentId && importedMomentIds.has(item.sourceMomentId)).length,
  };
  if (actual.moments !== expected.moments || actual.images !== expected.images || actual.closures !== expected.closures || actual.links !== expected.links) {
    throw new Error(`数量校验未通过：Moment ${actual.moments}/${expected.moments}，图片 ${actual.images}/${expected.images}，收束 ${actual.closures}/${expected.closures}，来源关系 ${actual.links}/${expected.links}`);
  }
  await cloud.markMigration(fingerprint, { ...expected, verified: actual });
  return { fingerprint, counts: { ...expected, verified: actual }, alreadyCompleted: false };
}
